/**
 * The gallery's thumbnail reader (the 2,000 gallery, docs/DESIGN-outfit-gallery-2000.md; rewritten 2026-10-11).
 *
 * What went wrong before: every tile read its own thumbnail (GET /v1/mascot/outfits/:id/thumbnail), four at a time,
 * and each read is one request under the desktop's device custody, which gyro-svc meters per device (240 a minute,
 * bursts of 120). Opening a gallery of 600 sent some 300 reads in a few seconds; the ones past the burst were refused
 * 429, the gallery marked them failed, and nothing read them again until a re-render happened to run the effect after
 * its one-minute hold, so tiles stayed blank, scattered through the groups.
 *
 * Now: tiles are read in batches (GET /v1/mascot/thumbnails?ids=…, up to MASCOT_THUMBNAILS_BATCH_MAX each), a bounded
 * number of requests at a time; a refusal or a failure is tried again on its own timer with a growing wait (a 429's
 * retry-after when it names one), so a blank tile fills in without anyone touching the page; a gyro-svc or desktop app
 * that does not know the batch route answers 404 (or 400), after which the reader falls back to single reads, six at a
 * time; and every thumbnail read is kept by its image hash for the whole page (a gallery, the picker under a chat reply
 * and a reload share them). Pure TypeScript with injected readers and timers, so it is tested without a DOM.
 */
import { MascotLookError } from './client.js';
import { MASCOT_THUMBNAILS_BATCH_MAX } from './mascotLooks.js';
export const MASCOT_THUMBNAIL_TIMING = Object.freeze({ retryBaseMs: 1_000, retryMaxMs: 30_000, attempts: 6, holdMs: 60_000 });
/** How many batch requests, or single reads, are in flight at once. */
export const MASCOT_THUMBNAIL_BATCHES_AT_ONCE = 2;
export const MASCOT_THUMBNAIL_SINGLES_AT_ONCE = 6;
/** Thumbnails kept, by image hash, at most (about 40 KB each as a data: URI). */
const THUMBNAILS_KEPT_MAX = 2_500;
const kept = new Map();
/** A thumbnail already read in this page, as a data: URI, by its image hash. */
export function rememberedThumbnail(sha256) {
    const uri = kept.get(sha256);
    if (uri !== undefined) {
        // Most recently used last: the oldest is forgotten first.
        kept.delete(sha256);
        kept.set(sha256, uri);
    }
    return uri;
}
export function rememberThumbnail(sha256, base64) {
    const uri = `data:image/png;base64,${base64}`;
    kept.delete(sha256);
    kept.set(sha256, uri);
    if (kept.size > THUMBNAILS_KEPT_MAX)
        kept.delete(kept.keys().next().value);
    return uri;
}
/** Test seam: forget every thumbnail read, so one test's reads do not answer the next. */
export function __forgetThumbnailsForTests() {
    kept.clear();
}
/** Why a read failed, from the error it threw: how long to wait, and whether the batch route is unknown to this gyro-svc or desktop app. */
function failureOf(error, batch) {
    if (error instanceof MascotLookError) {
        // A 404 (or 400) on the batch route from an older gyro-svc, or a desktop app that does not forward it: fall back to single reads.
        const routeUnknown = batch && (error.status === 404 || error.status === 400 || error.status === 405);
        return { waitMs: error.status === 429 ? error.retryAfterMs : undefined, routeUnknown };
    }
    return { waitMs: undefined, routeUnknown: false };
}
/**
 * Reads the thumbnails of the tiles it is told are wanted (setWanted), batched and bounded, retrying on its own timers,
 * and tells its subscribers whenever one landed. One per gallery; the thumbnails themselves are shared (rememberedThumbnail).
 */
export class MascotThumbnailLoader {
    readers;
    timing;
    now;
    schedule;
    cancel;
    /** id → image hash, for every wanted tile not yet read. */
    wanted = new Map();
    inFlight = new Set();
    /** By image hash: how many reads failed in a row and when the next may start. */
    retries = new Map();
    listeners = new Set();
    workers = 0;
    batchUnsupported;
    timer = null;
    notifying = false;
    disposed = false;
    constructor(readers, timing = MASCOT_THUMBNAIL_TIMING, now = () => Date.now(), 
    // Wrapped, never the bare functions: a browser's setTimeout called as a method of this loader throws "Illegal invocation".
    schedule = (fn, ms) => setTimeout(fn, ms), cancel = (timer) => clearTimeout(timer)) {
        this.readers = readers;
        this.timing = timing;
        this.now = now;
        this.schedule = schedule;
        this.cancel = cancel;
        this.batchUnsupported = !readers.many;
    }
    /** Whether single reads are in use because the batch route answered that it is unknown (tests and diagnostics). */
    get fellBackToSingles() {
        return this.batchUnsupported && Boolean(this.readers.many);
    }
    /** The thumbnail of an image, when read already. */
    get(sha256) {
        return rememberedThumbnail(sha256);
    }
    /** Called whenever a thumbnail landed (once per batch), so a gallery re-renders. */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => { this.listeners.delete(listener); };
    }
    /**
     * The tiles on screen whose thumbnail is still to be read: replaces the earlier set, so a tile that left the screen
     * (its group closed) is no longer read; a read in flight still lands and is kept. Starts reading at once.
     */
    setWanted(entries) {
        if (this.disposed)
            return;
        this.wanted.clear();
        for (const { id, sha256 } of entries) {
            if (rememberedThumbnail(sha256) === undefined)
                this.wanted.set(id, sha256);
        }
        this.pump();
    }
    /** How many tiles still wait for a read (tests). */
    get pending() {
        return this.wanted.size;
    }
    dispose() {
        this.disposed = true;
        this.wanted.clear();
        this.listeners.clear();
        if (this.timer !== null) {
            this.cancel(this.timer);
            this.timer = null;
        }
    }
    /** The wanted ids whose turn has come: not in flight, and past their retry wait. */
    ready(now) {
        const ids = [];
        for (const [id, sha256] of this.wanted) {
            if (this.inFlight.has(id))
                continue;
            const retry = this.retries.get(sha256);
            if (retry && retry.notBefore > now)
                continue;
            ids.push(id);
        }
        return ids;
    }
    pump() {
        if (this.disposed)
            return;
        const now = this.now();
        const ready = this.ready(now);
        const limit = this.batchUnsupported || !this.readers.many ? MASCOT_THUMBNAIL_SINGLES_AT_ONCE : MASCOT_THUMBNAIL_BATCHES_AT_ONCE;
        const size = this.batchUnsupported || !this.readers.many ? 1 : MASCOT_THUMBNAILS_BATCH_MAX;
        let at = 0;
        while (this.workers < limit && at < ready.length) {
            const ids = ready.slice(at, at + size);
            at += size;
            for (const id of ids)
                this.inFlight.add(id);
            this.workers += 1;
            void this.read(ids).finally(() => {
                this.workers -= 1;
                for (const id of ids)
                    this.inFlight.delete(id);
                this.pump();
            });
        }
        this.scheduleRetry();
    }
    /** One timer, for the earliest wait among the tiles still wanted but not yet due. */
    scheduleRetry() {
        if (this.timer !== null) {
            this.cancel(this.timer);
            this.timer = null;
        }
        const now = this.now();
        let earliest = Infinity;
        for (const [id, sha256] of this.wanted) {
            if (this.inFlight.has(id))
                continue;
            const retry = this.retries.get(sha256);
            if (retry && retry.notBefore > now && retry.notBefore < earliest)
                earliest = retry.notBefore;
        }
        if (earliest !== Infinity)
            this.timer = this.schedule(() => { this.timer = null; this.pump(); }, Math.max(1, earliest - now));
    }
    async read(ids) {
        const batch = !this.batchUnsupported && this.readers.many !== undefined;
        try {
            if (!batch && !this.readers.one) {
                // Nothing to read with: rest, in case a reader appears (a client swapped under the gallery).
                for (const id of ids)
                    this.giveUp(id);
                return;
            }
            if (batch) {
                const answer = await this.readers.many(ids);
                let landed = false;
                for (const [id, { sha256, thumbnailPng }] of Object.entries(answer.thumbnails)) {
                    // Kept under the hash gyro-svc names, and under the one the gallery knows (the same image: a view that is a step
                    // behind storage still finds it).
                    rememberThumbnail(sha256, thumbnailPng);
                    const known = this.wanted.get(id);
                    if (known !== undefined && known !== sha256)
                        rememberThumbnail(known, thumbnailPng);
                    this.settle(id);
                    landed = true;
                }
                // Not in the gallery: nothing to read until the gallery reloads; rests like a read that gave up.
                for (const id of answer.missing)
                    this.giveUp(id);
                for (const id of answer.failed)
                    this.failed(id, undefined);
                // An id the answer named nowhere (an answer of another shape, a route that dropped it): a failed read, so it waits
                // its turn instead of being read again at once, for ever (2026-10-15: a test's answer keyed by index did that).
                const named = new Set([...Object.keys(answer.thumbnails), ...answer.missing, ...answer.failed]);
                for (const id of ids)
                    if (!named.has(id))
                        this.failed(id, undefined);
                if (landed)
                    this.notify();
            }
            else {
                const id = ids[0];
                const base64 = await this.readers.one(id);
                const sha256 = this.wanted.get(id);
                if (sha256 !== undefined)
                    rememberThumbnail(sha256, base64);
                this.settle(id);
                this.notify();
            }
        }
        catch (error) {
            const { waitMs, routeUnknown } = failureOf(error, batch);
            if (routeUnknown && this.readers.one) {
                // Nothing was read and nothing is held against these tiles: the next pump reads them one at a time.
                this.batchUnsupported = true;
                return;
            }
            for (const id of ids)
                this.failed(id, waitMs);
        }
    }
    settle(id) {
        const sha256 = this.wanted.get(id);
        if (sha256 !== undefined)
            this.retries.delete(sha256);
        this.wanted.delete(id);
    }
    /** A read that failed: wait, longer each time, then try again; after `attempts` in a row, rest for holdMs and start over. */
    failed(id, waitMs) {
        const sha256 = this.wanted.get(id);
        if (sha256 === undefined)
            return;
        const now = this.now();
        const before = this.retries.get(sha256);
        const attempt = (before?.attempt ?? 0) + 1;
        if (attempt >= this.timing.attempts) {
            this.retries.set(sha256, { attempt: 0, notBefore: now + this.timing.holdMs });
            return;
        }
        const backoff = Math.min(this.timing.retryMaxMs, this.timing.retryBaseMs * 2 ** (attempt - 1));
        this.retries.set(sha256, { attempt, notBefore: now + Math.max(waitMs ?? 0, backoff) });
    }
    giveUp(id) {
        const sha256 = this.wanted.get(id);
        if (sha256 === undefined)
            return;
        this.retries.set(sha256, { attempt: 0, notBefore: this.now() + this.timing.holdMs });
    }
    /** Subscribers hear once per landing, after the current read's bookkeeping. */
    notify() {
        if (this.notifying || this.disposed)
            return;
        this.notifying = true;
        queueMicrotask(() => {
            this.notifying = false;
            if (this.disposed)
                return;
            for (const listener of this.listeners)
                listener();
        });
    }
}
//# sourceMappingURL=mascotThumbnails.js.map